// parseHeadersFromFile.js
// Return { type, headers, rows } for CSV / XLSX / JSON / JSONL

// ensure unique header names
const dedupe = (arr) => {
  const seen = {};
  return arr.map(h => {
    let name = (h || '').toString().trim() || 'col';
    let base = name, i = 1;
    while (seen[name]) name = `${base}_${i++}`;
    seen[name] = true;
    return name;
  });
};

// split CSV line (handles quotes)
const splitCSV = (line) => {
  const out = []; let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; continue; }
    if (ch === ',' && !inQ) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map(s => s.trim());
};

// flatten one level (user.screen_name)
const flat1 = (o) => {
  const f = {};
  Object.entries(o || {}).forEach(([k, v]) => {
    if (v && typeof v === 'object' && !Array.isArray(v))
      Object.entries(v).forEach(([k2, v2]) => f[`${k}.${k2}`] = v2);
    else f[k] = v;
  });
  return f;
};

// read file as text / buffer
const readAsText = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsText(file);
});
const readAsBuf = (file) => file.arrayBuffer();

// ---- parsers ----
const parseCSV = async (file, sampleRows) => {
  const txt = await readAsText(file);
  const lines = txt.split(/\r?\n/).filter(Boolean);
  const headers = dedupe(splitCSV(lines[0] || ''));
  const rows = lines.slice(1, 1 + sampleRows).map(l => {
    const cells = splitCSV(l), o = {};
    headers.forEach((h, i) => o[h] = cells[i] ?? null);
    return o;
  });
  return { type: 'csv', headers, rows };
};

const parseXLSX = async (file, sampleRows) => {
  const XLSX = await import('xlsx'); // lazy-load to keep bundle small
  const wb = XLSX.read(await readAsBuf(file), { type: 'array' });
  const sheets = wb.SheetNames.map(name => ({
    name,
    aoa: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true }),
  }));

  // Sprinklr exports put message and account fields on separate sheets. Use
  // the union of their real headers so the preset can map account-only fields
  // before the file reaches the backend merge step.
  const sprinklrSheets = sheets
    .map(sheet => ({ ...sheet, headerRow: findSprinklrHeaderRow(sheet.aoa) }))
    .filter(sheet => sheet.headerRow !== -1);

  const useSprinklrHeaders = sprinklrSheets.length > 0;
  const headerSources = useSprinklrHeaders
    ? sprinklrSheets
    : [{ ...sheets[0], headerRow: 0 }];
  const rawHeaders = headerSources.flatMap(({ aoa, headerRow }) => aoa[headerRow] || []);
  const headers = dedupe(rawHeaders.map(c => (c == null ? '' : String(c))));

  // Keep the preview rows from the first detected data sheet; headers above
  // are intentionally the union across all Sprinklr sheets.
  const previewSource = headerSources[0] || { aoa: [], headerRow: 0 };
  const rows = previewSource.aoa.slice(previewSource.headerRow + 1, previewSource.headerRow + 1 + sampleRows).map(row => {
    const o = {}; headers.forEach((h, i) => o[h] = row?.[i] ?? null); return o;
  });
  return { type: 'xlsx', headers, rows };
};

function findSprinklrHeaderRow(aoa) {
  for (let i = 0; i < Math.min(aoa.length, 10); i++) {
    const cells = (aoa[i] || []).map(c => String(c ?? '').trim().toLowerCase());
    const hasMessage = cells.includes('conversation stream');
    const hasCreated = cells.includes('created time');
    const hasType = cells.includes('message type');
    const hasAccount = cells.includes('screen name') && cells.includes('username');
    if ((hasMessage && hasCreated) || (hasCreated && hasType) || hasAccount) return i;
  }
  return -1;
}

const parseJSON = async (file, sampleRows, isJSONL) => {
  const txt = await readAsText(file);
  if (isJSONL) {
    const lines = txt.split(/\r?\n/).filter(Boolean).slice(0, sampleRows);
    const objs = lines.map(l => flat1(JSON.parse(l)));
    const headers = dedupe(Object.keys(objs[0] || {}));
    return { type: 'jsonl', headers, rows: objs };
  }
  const parsed = JSON.parse(txt);
  const arr = Array.isArray(parsed) ? parsed.slice(0, sampleRows) : [parsed];
  const objs = arr.map(flat1);
  const headers = dedupe(Object.keys(objs[0] || {}));
  return { type: 'json', headers, rows: objs };
};

// ---- main entry ----
export async function parseHeadersFromFile(file, sampleRows = 5) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (ext === 'csv') return parseCSV(file, sampleRows);
  if (ext === 'xlsx' || ext === 'xls') return parseXLSX(file, sampleRows);
  if (ext === 'json') return parseJSON(file, sampleRows, false);
  if (ext === 'jsonl') return parseJSON(file, sampleRows, true);
  throw new Error(`Unsupported file type: .${ext}`);
}
